import type { Material, MaterialCategory } from '@/lib/types'

/**
 * Port of the app's `CategoryIndex` (`features/materials/domain/CategoryIndex.kt`) — keep both in
 * sync. Resolves a category reference — a material's `categoryId` + `category`, or a question's /
 * repeater field's `materialCategoryId` + `materialCategory` — to the catalog entry.
 *
 * The id is the link; the stored name is only a copy (old app builds, CSV). Documents written before
 * ids existed carry the name only, so a reference resolves by id when it names an existing category,
 * otherwise by name — exact (trimmed) first, then ignoring case.
 *
 * `key` turns a reference into a match key: two references point at the same category when their
 * keys are equal. An unresolvable reference keeps its own id (deleted category) or, with no id, its
 * trimmed name (legacy free text), so leftovers still match each other as they did before ids. A
 * blank reference has a blank key and matches nothing.
 */
export class CategoryIndex {
  private readonly byId = new Map<string, MaterialCategory>()
  private readonly byName = new Map<string, MaterialCategory>()
  private readonly byLowerName = new Map<string, MaterialCategory>()

  constructor(categories: readonly MaterialCategory[]) {
    for (const category of categories) {
      this.byId.set(category.id, category)
      // First one wins, as `reversed().associateBy` does in Kotlin.
      const name = category.name.trim()
      if (!this.byName.has(name)) this.byName.set(name, category)
      const lower = name.toLowerCase()
      if (!this.byLowerName.has(lower)) this.byLowerName.set(lower, category)
    }
  }

  /** The catalog category a reference points at, or null (deleted category / free text / blank). */
  find(categoryId: string, categoryName: string): MaterialCategory | null {
    const byId = this.byId.get(categoryId.trim())
    if (byId) return byId
    const name = categoryName.trim()
    if (name === '') return null
    return this.byName.get(name) ?? this.byLowerName.get(name.toLowerCase()) ?? null
  }

  /** Match key of a reference; blank for a blank reference. */
  key(categoryId: string, categoryName: string): string {
    const found = this.find(categoryId, categoryName)
    if (found) return `id:${found.id}`
    const id = categoryId.trim()
    if (id !== '') return `id:${id}`
    const name = categoryName.trim()
    return name === '' ? '' : `name:${name}`
  }

  /** The category's current name, else the stored copy (a renamed category shows its new name). */
  displayName(categoryId: string, categoryName: string): string {
    return this.find(categoryId, categoryName)?.name ?? categoryName
  }

  /** The id to store for a reference picked by name, else `fallbackId` (unresolvable name). */
  idForName(categoryName: string, fallbackId = ''): string {
    if (categoryName.trim() === '') return ''
    return this.find('', categoryName)?.id ?? fallbackId
  }

  /** Materials grouped by `key` of their category reference; materials without one are dropped. */
  groupMaterials(materials: readonly Material[]): Map<string, Material[]> {
    const groups = new Map<string, Material[]>()
    for (const material of materials) {
      const key = this.key(material.categoryId, material.category)
      if (key === '') continue
      const list = groups.get(key)
      if (list) list.push(material)
      else groups.set(key, [material])
    }
    return groups
  }
}
